import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { FloppyDisk, Plug, CheckCircle, XCircle } from "@phosphor-icons/react";

export default function Settings() {
  const [tallyUrl, setTallyUrl] = useState("");
  const [interval, setInterval] = useState(30);
  const [mode, setMode] = useState("xml");
  const [apiUrl, setApiUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setTallyUrl(r.data.tally_url || "");
      // If auto-sync was previously turned off, reflect that as the "Off"
      // option in the dropdown regardless of whatever interval was last saved.
      const autoOn = r.data.auto_sync_enabled ?? true;
      setInterval(autoOn ? (r.data.sync_interval_minutes || 30) : 0);
      setMode(r.data.sync_mode || "xml");
      setApiUrl(r.data.third_party_api_url || "");
    }).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // The Sync Interval dropdown now carries its own "Off" option (value 0).
      // Picking it disables the scheduler outright; picking any real interval
      // re-enables it — so this one control fully drives auto_sync_enabled,
      // no separate toggle needed.
      await api.put("/settings", {
        tally_url: tallyUrl, sync_interval_minutes: interval || 30, sync_mode: mode,
        third_party_api_url: apiUrl, third_party_api_key: apiKey, auto_sync_enabled: interval !== 0,
      });
      toast.success("Settings saved. Scheduler updated.");
      setApiKey("");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };

  const testConnection = async () => {
    setTesting(true); setPreview(null);
    try {
      const { data } = await api.post("/sync/preview", {
        tally_url: tallyUrl, sync_mode: mode,
        third_party_api_url: apiUrl, third_party_api_key: apiKey || null,
      });
      setPreview(data);
      if (data.ok) toast.success(`Connected — parsed ${data.count} vouchers`);
      else toast.error(data.error || "Test failed");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setTesting(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Tally connection, sync scheduler & credentials.</p>
      </div>

      <form onSubmit={save} className="brut-card p-6 space-y-5" data-testid="settings-form">
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-muted-foreground">Tally Server URL</span>
          <input
            value={tallyUrl}
            onChange={(e) => setTallyUrl(e.target.value)}
            placeholder="http://cloud5.technamami.com:1003"
            data-testid="tally-url-input"
            className="w-full mt-1.5 h-11 px-3 rounded-md border border-border bg-card font-mono text-sm focus:outline-none focus:ring-2 focus:ring-signal/40"
          />
        </label>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Sync Interval</span>
            <select
              value={interval}
              onChange={(e) => setInterval(Number(e.target.value))}
              data-testid="sync-interval-select"
              className="w-full mt-1.5 h-11 px-3 rounded-md border border-border bg-card text-sm"
            >
              <option value={15}>Every 15 minutes</option>
              <option value={30}>Every 30 minutes</option>
              <option value={60}>Every 1 hour</option>
              <option value={0}>Off (disable auto-sync)</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Sync Mode</span>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              data-testid="sync-mode-select"
              className="w-full mt-1.5 h-11 px-3 rounded-md border border-border bg-card text-sm"
            >
              <option value="xml">Direct XML (Tally HTTP Gateway)</option>
              <option value="api">Third-Party JSON API (API2Books)</option>
            </select>
          </label>
        </div>

        {mode === "api" && (
          <div className="space-y-4 border-t border-border pt-5">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">Third-Party API URL</span>
              <input
                value={apiUrl}
                onChange={(e) => setApiUrl(e.target.value)}
                placeholder="https://api2books.com/v1/vouchers"
                data-testid="third-party-url-input"
                className="w-full mt-1.5 h-11 px-3 rounded-md border border-border bg-card font-mono text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">API Key (stored encrypted)</span>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Leave blank to keep existing"
                data-testid="third-party-key-input"
                className="w-full mt-1.5 h-11 px-3 rounded-md border border-border bg-card font-mono text-sm"
              />
            </label>
          </div>
        )}

        {interval === 0 && (
          <div className="text-xs text-signal dark:text-signal -mt-2">
            Auto-sync is off — Tally data will only update when you click "Sync Now" on the Sync Log page.
          </div>
        )}

        <div className="flex flex-wrap justify-between items-center gap-3 pt-2 border-t border-border">
          <button type="button" onClick={testConnection} disabled={testing} data-testid="test-connection-button"
            className="h-10 px-4 rounded-md border border-border text-sm font-semibold inline-flex items-center gap-2 hover:bg-muted disabled:opacity-60">
            <Plug size={16} weight="bold" /> {testing ? "Testing..." : "Test Connection & Fetch Preview"}
          </button>
          <button type="submit" disabled={busy} data-testid="save-settings-button"
            className="signal-btn h-10 px-5 rounded-md text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
            <FloppyDisk size={16} weight="bold" /> {busy ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </form>

      {preview && (
        <div className="brut-card p-5 space-y-3" data-testid="preview-panel">
          <div className="flex items-center gap-2">
            {preview.ok ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs border border-signal/40 bg-signal/10 text-signal dark:text-signal">
                <CheckCircle size={14} weight="fill" /> Connected
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs border border-signal/40 bg-signal/10 text-signal dark:text-signal">
                <XCircle size={14} weight="fill" /> {preview.error}
              </span>
            )}
            {preview.ok && <span className="text-xs text-muted-foreground font-mono">{preview.mode.toUpperCase()} • {preview.endpoint}</span>}
          </div>
          {preview.detail && <div className="text-xs font-mono text-signal dark:text-signal whitespace-pre-wrap">{preview.detail}</div>}
          {preview.hint && <div className="text-xs text-muted-foreground">{preview.hint}</div>}
          {preview.ok && (
            <>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Parsed Vouchers ({preview.count}) — first 5</div>
              <pre className="font-mono text-xs bg-muted p-3 rounded-md overflow-x-auto max-h-64">{JSON.stringify(preview.parsed_sample, null, 2)}</pre>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Raw Response Sample</div>
              <pre className="font-mono text-xs bg-muted p-3 rounded-md overflow-x-auto max-h-64">{typeof preview.raw_sample === "string" ? preview.raw_sample : JSON.stringify(preview.raw_sample, null, 2)}</pre>
            </>
          )}
        </div>
      )}
    </div>
  );
}
